import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ExternalLink, Plus, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import {
  formatDate, formatDateTime, formatNumber, METRIC_KEYS, METRIC_LABELS, PLATFORM_METRIC_NOTE,
} from "@/lib/format";
import { useAuth } from "@/lib/AuthContext";
import { PlatformBadge, ReviewBadge } from "@/components/portal/PlatformBits";
import { DeleteConfirmModal, RemarkCell } from "@/components/portal/InternalBits";
import { AdjustedBadge, CorrectionModal } from "@/components/portal/CorrectionBits";
import {
  Attachment, Badge, Button, Empty, ErrorNote, Field, Loading, Modal, Note, SecureImage,
  inputClass,
} from "@/components/portal/Ui";
import { InsightModal, PostModal } from "@/pages/creator/CreatorCampaigns";

const PLATFORMS = ["Instagram", "TikTok", "XiaoHongShu", "Threads"];
const REVIEW_FILTERS = ["Pending review", "Approved", "Changes requested"];

/** Every content submission on one campaign, with the full internal action set. */
export function SubmissionsSection({ campaign, creators, onChanged }) {
  const campaignId = campaign.id;
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState(null);
  const [error, setError] = useState("");

  const creatorId = params.get("creator") || "";
  const platform = params.get("platform") || "";
  const insightStatus = params.get("insight_status") || "";
  const reviewStatus = params.get("review_status") || "";
  const hasRemark = params.get("has_remark") === "1";
  const showDeleted = params.get("deleted") === "1";
  const search = params.get("q") || "";

  const setFilter = (key, value) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true }
    );

  const [postModal, setPostModal] = useState(null);
  const [newPostCreator, setNewPostCreator] = useState("");
  const [insightModal, setInsightModal] = useState(null);
  const [flagTarget, setFlagTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [correctionTarget, setCorrectionTarget] = useState(null);

  const screensFor = params.get("screens") || "";

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/posts", {
        params: {
          campaign_id: campaignId,
          creator_id: creatorId || undefined,
          platform: platform || undefined,
          insight_status: insightStatus || undefined,
          review_status: reviewStatus || undefined,
          has_remark: hasRemark || undefined,
          include_deleted: showDeleted || undefined,
          search: search || undefined,
        },
      });
      setPosts(data);
      setError("");
    } catch (e) {
      setError(apiError(e));
      setPosts((prev) => prev || []);
    }
  }, [campaignId, creatorId, platform, insightStatus, reviewStatus, hasRemark, showDeleted, search]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = async () => {
    await load();
    await onChanged?.();
  };

  const review = async (post, status, note) => {
    try {
      await api.post(`/posts/${post.id}/review`, { status, note: note || null });
      toast.success(
        status === "Approved"
          ? "Submission approved — the client can now see it"
          : `Submission marked ${status.toLowerCase()}`
      );
      setReviewTarget(null);
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const resolveFlag = async (insightId) => {
    try {
      await api.post(`/insights/${insightId}/resolve`);
      toast.success("Correction resolved — metrics are back in client reporting");
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveRemark = async (post, remark) => {
    try {
      await api.put(`/posts/${post.id}/remark`, { remark });
      toast.success(remark ? "Remark saved" : "Remark cleared");
      await refresh();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const removePost = async (reason) => {
    try {
      await api.delete(`/posts/${deleteTarget.id}`, { data: { reason } });
      toast.success("Submission deleted — it no longer appears anywhere");
      await refresh();
      return true;
    } catch (e) {
      toast.error(apiError(e));
      return false;
    }
  };

  const restorePost = async (post) => {
    try {
      await api.post(`/posts/${post.id}/restore`);
      toast.success("Submission restored");
      await refresh();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const screenshotView = (posts || []).find((p) => p.id === screensFor)?.latest_insight || null;

  if (posts === null) return <Loading label="Loading submissions…" />;

  return (
    <div data-testid="campaign-submissions-section">
      <ErrorNote message={error} onRetry={load} />

      <div className="rounded-xl border border-[#EFEAE1] bg-[#FBFAF7] p-4" data-testid="submission-filters">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Field label="KOL">
            <select
              value={creatorId}
              onChange={(e) => setFilter("creator", e.target.value)}
              className={inputClass}
              data-testid="filter-creator"
            >
              <option value="">All KOLs</option>
              {(creators || []).map((creator) => (
                <option key={creator.creator_id} value={creator.creator_id}>
                  {creator.creator_name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Platform">
            <select
              value={platform}
              onChange={(e) => setFilter("platform", e.target.value)}
              className={inputClass}
              data-testid="filter-platform"
            >
              <option value="">All platforms</option>
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Insight status">
            <select
              value={insightStatus}
              onChange={(e) => setFilter("insight_status", e.target.value)}
              className={inputClass}
              data-testid="filter-insight-status"
            >
              <option value="">Any</option>
              <option value="reported">Reported</option>
              <option value="missing">Missing insights</option>
              <option value="needs_correction">Needs correction</option>
            </select>
          </Field>
          <Field label="Review status">
            <select
              value={reviewStatus}
              onChange={(e) => setFilter("review_status", e.target.value)}
              className={inputClass}
              data-testid="filter-review-status"
            >
              <option value="">Any</option>
              {REVIEW_FILTERS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Search">
            <input
              value={search}
              onChange={(e) => setFilter("q", e.target.value)}
              placeholder="KOL or link"
              className={inputClass}
              data-testid="filter-search"
            />
          </Field>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-5">
          <label className="flex items-center gap-2.5 text-sm font-semibold">
            <input
              type="checkbox"
              checked={hasRemark}
              onChange={(e) => setFilter("has_remark", e.target.checked ? "1" : "")}
              className="h-4 w-4 accent-[#F2811D]"
              data-testid="filter-has-remark"
            />
            Has an internal remark
          </label>
          {isAdmin && (
            <label className="flex items-center gap-2.5 text-sm font-semibold">
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setFilter("deleted", e.target.checked ? "1" : "")}
                className="h-4 w-4 accent-[#F2811D]"
                data-testid="filter-show-deleted"
              />
              Show deleted submissions
            </label>
          )}
          <Button
            className="ml-auto"
            testId="internal-add-post-button"
            onClick={() => {
              setNewPostCreator("");
              setPostModal(campaign);
            }}
          >
            <Plus className="h-4 w-4" />
            Add post on behalf
          </Button>
        </div>
      </div>

      {posts.length === 0 ? (
        <Empty
          testId="performance-empty"
          title="No submissions match these filters"
          body="Clear a filter, or add a post on behalf of a KOL."
        />
      ) : (
        <div className="mt-4">
          <p className="mb-3 text-xs leading-relaxed body-text" data-testid="platform-metric-note">
            {PLATFORM_METRIC_NOTE}
          </p>
          <div className="scroll-x">
            <table className="w-full min-w-[1180px] text-sm">
              <thead>
                <tr className="border-b border-[#EFEAE1] text-left text-xs font-bold uppercase tracking-[0.08em] text-[#8a8378]">
                  <th className="pb-2.5 pr-4">Creator</th>
                  <th className="pb-2.5 pr-4">Platform</th>
                  <th className="pb-2.5 pr-4">Link</th>
                  <th className="pb-2.5 pr-4">Posted</th>
                  {METRIC_KEYS.map((key) => (
                    <th key={key} className="pb-2.5 pr-4 text-right">
                      {METRIC_LABELS[key]}
                    </th>
                  ))}
                  <th className="pb-2.5 pr-4">Screens</th>
                  <th className="pb-2.5 pr-4">Captured</th>
                  <th className="pb-2.5 pr-4">Updated</th>
                  <th className="pb-2.5 pr-4">Source</th>
                  <th className="pb-2.5 pr-4">Remark</th>
                  <th className="pb-2.5">Review &amp; actions</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => {
                  const insight = post.latest_insight;
                  return (
                    <tr
                      key={post.id}
                      className={`border-b border-[#F5F1EA] align-top ${
                        post.is_deleted
                          ? "bg-[#FDF3F3]"
                          : post.internal_remark
                            ? "bg-[#FFFDF6]"
                            : ""
                      }`}
                      data-testid={`performance-row-${post.id}`}
                    >
                      <td className="py-3 pr-4 font-semibold">
                        {post.creator_name}
                        {post.is_adjusted && (
                          <span className="mt-1 block">
                            <AdjustedBadge post={post} testId={`adjusted-badge-${post.id}`} />
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <PlatformBadge
                          platform={post.platform}
                          testId={`performance-platform-${post.id}`}
                        />
                      </td>
                      <td className="py-3 pr-4">
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noreferrer noopener"
                          data-testid={`performance-link-${post.id}`}
                          className="inline-flex items-center gap-1.5 font-semibold text-[#F2811D] hover:underline"
                        >
                          Open <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {formatDate(post.posting_date)}
                      </td>
                      {METRIC_KEYS.map((key) => (
                        <td key={key} className="py-3 pr-4 text-right tabular">
                          {insight?.[key] == null ? "—" : formatNumber(insight[key])}
                        </td>
                      ))}
                      <td className="py-3 pr-4">
                        {insight?.screenshots?.length ? (
                          <a
                            href={`?${(() => {
                              const next = new URLSearchParams(params);
                              next.set("screens", post.id);
                              return next.toString();
                            })()}`}
                            onClick={(event) => {
                              if (event.metaKey || event.ctrlKey || event.shiftKey) return;
                              event.preventDefault();
                              setFilter("screens", post.id);
                            }}
                            data-testid={`view-screenshots-${post.id}`}
                            className="font-semibold text-[#F2811D] hover:underline"
                          >
                            {insight.screenshots.length} file
                            {insight.screenshots.length === 1 ? "" : "s"}
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap">
                        {insight?.capture_date ? formatDate(insight.capture_date) : "—"}
                      </td>
                      <td className="py-3 pr-4 whitespace-nowrap text-xs body-text">
                        {formatDateTime(insight?.created_at || post.updated_at)}
                      </td>
                      <td className="py-3 pr-4 text-xs body-text">
                        {insight?.source || post.source}
                      </td>
                      <td className="py-3 pr-4">
                        <RemarkCell
                          value={post.internal_remark}
                          byName={post.internal_remark_by_name}
                          at={post.internal_remark_at}
                          onSave={(text) => saveRemark(post, text)}
                          testId={`post-remark-${post.id}`}
                        />
                      </td>
                      <td className="py-3">
                        <div className="flex flex-col gap-1.5">
                          <ReviewBadge
                            status={post.review_status}
                            testId={`review-badge-${post.id}`}
                          />
                          {post.review_status !== "Approved" && (
                            <button
                              type="button"
                              onClick={() => review(post, "Approved")}
                              data-testid={`approve-submission-${post.id}`}
                              className="text-left text-xs font-semibold text-[#1B6B3A] hover:underline"
                            >
                              Approve submission
                            </button>
                          )}
                          {post.review_status !== "Changes requested" && (
                            <button
                              type="button"
                              onClick={() => setReviewTarget(post)}
                              data-testid={`request-changes-${post.id}`}
                              className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                            >
                              Request changes
                            </button>
                          )}
                          {insight ? (
                            <Badge
                              tone={insight.needs_correction ? "red" : "green"}
                              testId={`insight-badge-${post.id}`}
                            >
                              {insight.needs_correction ? "Needs correction" : "Reported"}
                            </Badge>
                          ) : (
                            <Badge tone="neutral">No insights yet</Badge>
                          )}
                          <button
                            type="button"
                            onClick={() => setInsightModal({ campaign, post })}
                            data-testid={`internal-add-insight-${post.id}`}
                            className="text-left text-xs font-semibold text-[#F2811D] hover:underline"
                          >
                            Add snapshot
                          </button>
                          {insight && !insight.needs_correction && (
                            <button
                              type="button"
                              onClick={() => setFlagTarget(insight)}
                              data-testid={`flag-insight-${post.id}`}
                              className="text-left text-xs font-semibold text-[#B3261E] hover:underline"
                            >
                              Needs correction
                            </button>
                          )}
                          {insight?.needs_correction && (
                            <button
                              type="button"
                              onClick={() => resolveFlag(insight.id)}
                              data-testid={`resolve-insight-${post.id}`}
                              className="text-left text-xs font-semibold text-[#1B6B3A] hover:underline"
                            >
                              Resolve correction
                            </button>
                          )}
                          {isAdmin && !post.is_deleted && (
                            <button
                              type="button"
                              onClick={() => setCorrectionTarget(post)}
                              data-testid={`correct-post-${post.id}`}
                              className="text-left text-xs font-semibold text-[#2C4D8F] hover:underline"
                            >
                              Correct submission
                            </button>
                          )}
                          {isAdmin && !post.is_deleted && (
                            <button
                              type="button"
                              onClick={() => setDeleteTarget(post)}
                              data-testid={`delete-post-${post.id}`}
                              className="mt-1 inline-flex items-center gap-1.5 text-left text-xs font-semibold text-[#B3261E] hover:underline"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                              Delete submission
                            </button>
                          )}
                          {post.is_deleted && (
                            <div className="text-xs" data-testid={`deleted-note-${post.id}`}>
                              <p className="font-semibold text-[#B3261E]">
                                Deleted by {post.deleted_by_name}
                              </p>
                              <p className="body-text">{post.delete_reason}</p>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => restorePost(post)}
                                  data-testid={`restore-post-${post.id}`}
                                  className="mt-1 inline-flex items-center gap-1.5 font-semibold text-[#1B6B3A] hover:underline"
                                >
                                  <RotateCcw className="h-3.5 w-3.5" />
                                  Restore
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs body-text">
            Posts marked "Needs correction" are excluded from client totals until an AnyTribe staff
            member resolves the flag. Clients only ever see a neutral "Update pending" label.
          </p>
        </div>
      )}

      <PostModal
        campaign={postModal}
        creators={postModal?.creators || creators || []}
        creatorId={newPostCreator}
        setCreatorId={setNewPostCreator}
        onClose={() => setPostModal(null)}
        onSaved={async () => {
          setPostModal(null);
          await refresh();
        }}
      />
      <InsightModal
        state={insightModal}
        posts={posts}
        onClose={() => setInsightModal(null)}
        onSaved={async () => {
          setInsightModal(null);
          await refresh();
        }}
      />
      <ReviewModal
        post={reviewTarget}
        onClose={() => setReviewTarget(null)}
        onSubmit={(note) => review(reviewTarget, "Changes requested", note)}
      />
      <FlagModal
        insight={flagTarget}
        onClose={() => setFlagTarget(null)}
        onSaved={async () => {
          setFlagTarget(null);
          await refresh();
        }}
      />
      <CorrectionModal
        post={correctionTarget}
        onClose={() => setCorrectionTarget(null)}
        onSaved={async () => {
          setCorrectionTarget(null);
          await refresh();
        }}
      />
      <DeleteConfirmModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Delete this content submission?"
        summary={
          deleteTarget
            ? `${deleteTarget.creator_name} · ${deleteTarget.platform} · ${deleteTarget.url}`
            : ""
        }
        warning="The post and its insight snapshots disappear from internal tables, the client dashboard, all totals and report exports. An admin can restore it from the 'Show deleted submissions' view."
        onConfirm={removePost}
        testId="delete-post-modal"
      />
      <Modal
        open={Boolean(screenshotView)}
        onClose={() => setFilter("screens", "")}
        title="Insight screenshots"
        wide
        testId="screenshot-modal"
      >
        <div className="space-y-4">
          <Note tone="neutral">
            Private to AnyTribe and the creator. Captured {formatDate(screenshotView?.capture_date)}.
          </Note>
          {screenshotView?.screenshots?.map((file) => (
            <div key={file.id} className="rounded-xl border border-[#EFEAE1] p-3">
              <Attachment file={file} testId={`screenshot-link-${file.id}`} />
              {(file.content_type || "").startsWith("image/") && (
                <SecureImage
                  fileId={file.id}
                  alt={file.original_filename}
                  className="mt-3 max-h-[60vh] w-full rounded-lg object-contain"
                />
              )}
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}

function ReviewModal({ post, onClose, onSubmit }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setNote(post?.review_note || "");
  }, [post]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    await onSubmit(note.trim());
    setBusy(false);
  };

  return (
    <Modal open={Boolean(post)} onClose={onClose} title="Request changes" testId="review-modal">
      <form onSubmit={submit} className="space-y-4" data-testid="review-form">
        <Note tone="orange">
          The creator sees this note on their submission, and the client will not see the submission
          until it is approved.
        </Note>
        <Field label="What needs changing?" required>
          <textarea
            rows={3}
            required
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className={inputClass}
            placeholder="e.g. Please repost with the campaign hashtag in the first line."
            data-testid="review-note-input"
          />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="review-cancel-button">
            Cancel
          </Button>
          <Button type="submit" variant="danger" busy={busy} testId="review-submit-button">
            Request changes
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function FlagModal({ insight, onClose, onSaved }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setMessage("");
    setError("");
  }, [insight]);

  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/insights/${insight.id}/flag`, { message: message.trim() });
      toast.success("Marked as needing correction");
      onSaved();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={Boolean(insight)}
      onClose={onClose}
      title="Mark insight as needing correction"
      testId="flag-modal"
    >
      <form onSubmit={submit} className="space-y-4" data-testid="flag-form">
        <ErrorNote message={error} />
        <Note tone="orange">
          This post's metrics will be excluded from client totals until the flag is resolved.
          Clients see a neutral "Update pending" label — never your feedback.
        </Note>
        <Field label="Feedback for the creator" required>
          <textarea
            rows={4}
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Explain exactly what needs re-checking."
            className={inputClass}
            data-testid="flag-message-input"
          />
        </Field>
        <div className="flex justify-end gap-2.5">
          <Button variant="outline" onClick={onClose} testId="flag-cancel-button">
            Cancel
          </Button>
          <Button type="submit" busy={busy} testId="flag-submit-button">
            Mark as needing correction
          </Button>
        </div>
      </form>
    </Modal>
  );
}
